import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Zap, Plus, X, ExternalLink } from 'lucide-react';
import { api } from '../lib/api.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal } from '../components/ui/Modal.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SchemaField {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object';
  required: boolean;
  description?: string;
}

interface EventTypeEntry {
  id: string;
  eventType: string;
  displayName: string | null;
  description: string | null;
  schemaJson: SchemaField[] | null;
  isSystem: boolean;
  source: 'auto_discovered' | 'user_defined';
  firstSeenAt: string;
  lastSeenAt: string;
  totalCount: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelative(d: string) {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function sourceBadge(entry: EventTypeEntry) {
  if (entry.isSystem)
    return <Badge variant="default">system</Badge>;
  if (entry.source === 'user_defined')
    return <Badge variant="success">defined</Badge>;
  return <Badge variant="warning">auto-discovered</Badge>;
}

const FIELD_TYPES = ['string', 'number', 'boolean', 'object'] as const;

// ─── Promote / Edit Modal ─────────────────────────────────────────────────────

interface PromoteModalProps {
  entry: EventTypeEntry | null;
  onClose: () => void;
  onSaved: () => void;
}

function PromoteModal({ entry, onClose, onSaved }: PromoteModalProps) {
  const [displayName, setDisplayName] = useState(entry?.displayName ?? '');
  const [description, setDescription] = useState(entry?.description ?? '');
  const [fields, setFields] = useState<SchemaField[]>(entry?.schemaJson ?? []);

  // Reset form when entry changes
  const [prevEntry, setPrevEntry] = useState(entry);
  if (entry !== prevEntry) {
    setPrevEntry(entry);
    setDisplayName(entry?.displayName ?? '');
    setDescription(entry?.description ?? '');
    setFields(entry?.schemaJson ?? []);
  }

  const mutation = useMutation({
    mutationFn: () =>
      api
        .put(`/api/v1/signals/types/${encodeURIComponent(entry!.eventType)}`, {
          displayName: displayName.trim() || undefined,
          description: description.trim() || undefined,
          schemaJson: fields.length > 0 ? fields : null,
        })
        .then((r) => r.data),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  function addField() {
    setFields((prev) => [...prev, { name: '', type: 'string', required: false }]);
  }

  function removeField(i: number) {
    setFields((prev) => prev.filter((_, idx) => idx !== i));
  }

  function updateField(i: number, patch: Partial<SchemaField>) {
    setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  }

  const isEdit = entry?.source === 'user_defined';

  return (
    <Modal
      open={!!entry}
      onClose={onClose}
      title={isEdit ? `Edit: ${entry?.eventType}` : `Promote: ${entry?.eventType}`}
      description={
        isEdit
          ? 'Update the display name, description, or schema for this signal type.'
          : 'Add a friendly name and optional schema to formalize this auto-discovered signal type.'
      }
      size="lg"
    >
      <div className="space-y-5">
        {/* Display name */}
        <div>
          <label className="block text-xs font-medium text-zinc-600 mb-1.5">
            Display Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Article Read"
            className="w-full h-9 px-3 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-zinc-600 mb-1.5">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="When is this signal emitted? What does it signify?"
            rows={2}
            className="w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none"
          />
        </div>

        {/* Schema fields */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-zinc-600">Schema Fields</label>
            <button
              type="button"
              onClick={addField}
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 transition-colors"
            >
              <Plus size={12} />
              Add field
            </button>
          </div>

          {fields.length === 0 ? (
            <p className="text-xs text-zinc-400 italic py-1">
              No fields defined — payload accepted as-is.
            </p>
          ) : (
            <div className="space-y-2">
              {fields.map((field, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={field.name}
                    onChange={(e) => updateField(i, { name: e.target.value })}
                    placeholder="field name"
                    className="flex-1 h-8 px-2.5 border border-zinc-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                  <select
                    value={field.type}
                    onChange={(e) =>
                      updateField(i, { type: e.target.value as SchemaField['type'] })
                    }
                    className="h-8 px-2 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-zinc-500 whitespace-nowrap cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateField(i, { required: e.target.checked })}
                      className="rounded"
                    />
                    required
                  </label>
                  <input
                    type="text"
                    value={field.description ?? ''}
                    onChange={(e) => updateField(i, { description: e.target.value })}
                    placeholder="description (optional)"
                    className="flex-1 h-8 px-2.5 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                  <button
                    type="button"
                    onClick={() => removeField(i)}
                    className="text-zinc-300 hover:text-red-400 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {mutation.isError && (
          <p className="text-xs text-red-500">
            Failed to save. Please try again.
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!displayName.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="h-9 px-4 text-sm bg-zinc-900 text-white rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Promote'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SignalTypesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [modalEntry, setModalEntry] = useState<EventTypeEntry | null>(null);
  const [sourceFilter, setSourceFilter] = useState(searchParams.get('source') ?? '');

  const { data, isLoading } = useQuery<{ items: EventTypeEntry[]; total: number }>({
    queryKey: ['signal-types', sourceFilter],
    queryFn: () =>
      api
        .get('/api/v1/signals/types', {
          params: {
            limit: 500,
            ...(sourceFilter ? { source: sourceFilter } : {}),
          },
        })
        .then((r) => r.data),
  });

  const entries = data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Signal Types"
        description="All known signal types — built-in, auto-discovered, and promoted custom types"
        action={
          <span className="text-xs text-zinc-400 font-medium tabular-nums">
            {data?.total ?? 0} types
          </span>
        }
      />

      {/* Source filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="h-9 pl-3 pr-8 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900 appearance-none"
          >
            <option value="">All sources</option>
            <option value="auto_discovered">Auto-discovered</option>
            <option value="user_defined">Defined</option>
          </select>
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">
            ▾
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Signal Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Display Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Source
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Count
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Last Seen
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={8} cols={6} />
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState
                    icon={<Zap size={26} />}
                    title="No signal types found"
                    description="Signal types appear here as signals are ingested via POST /api/v1/signals."
                  />
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-zinc-50/70 transition-colors">
                  <td className="px-4 py-2.5">
                    <code className="text-xs font-mono text-zinc-800 bg-zinc-50 px-1.5 py-0.5 rounded">
                      {entry.eventType}
                    </code>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-zinc-600">
                    {entry.displayName ?? <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5">{sourceBadge(entry)}</td>
                  <td className="px-4 py-2.5 text-sm text-zinc-500 tabular-nums">
                    {entry.totalCount.toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-zinc-400">
                    {formatRelative(entry.lastSeenAt)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {!entry.isSystem && (
                        <button
                          onClick={() => setModalEntry(entry)}
                          className="text-xs text-zinc-500 hover:text-zinc-800 transition-colors"
                        >
                          {entry.source === 'user_defined' ? 'Edit' : 'Promote'}
                        </button>
                      )}
                      <button
                        onClick={() =>
                          navigate(`/signals?eventType=${encodeURIComponent(entry.eventType)}`)
                        }
                        className="flex items-center gap-1 text-xs text-sky-500 hover:text-sky-700 transition-colors"
                      >
                        <ExternalLink size={11} />
                        View signals
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PromoteModal
        entry={modalEntry}
        onClose={() => setModalEntry(null)}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['signal-types'] });
        }}
      />
    </div>
  );
}
